import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { provideQueryDevTools } from '@ngneat/query-devtools';

import { MqttModule, MqttServiceConfig, MqttClientService } from 'ngx-mqtt';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PortfoliosService } from './services/portfolios/portfolios.service';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader, TranslateCompiler } from '@ngx-translate/core';

import { LoadConfigurationService } from './services/common/load-configuration.service';
import { IonicStorageModule } from '@ionic/storage-angular';
import * as SentrySibling from '@sentry/angular-ivy';
import { SharedModule } from './app.shared.module';
import { STORAGE_NAME, DRIVER_ORDER } from './shared-test-modules';
import { AddDeviceModalPageModule } from './pages/modals/add-device-configuration/add-device-modal/add-device-modal.module';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { QueryClient, provideQueryClient } from '@ngneat/query';

export function createTranslationLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function delayBootstrapping(loadConfig: LoadConfigurationService) {
  return () => loadConfig.initialise();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
    },
  },
});

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({
      innerHTMLTemplatesEnabled: true,
    }),
    IonicStorageModule.forRoot({ name: STORAGE_NAME, driverOrder: DRIVER_ORDER }),
    AppRoutingModule,
    HttpClientModule,
    SharedModule,
    MqttModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      // enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslationLoader,
        deps: [HttpClient],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    AddDeviceModalPageModule,
  ],
  providers: [
    { provide: ErrorHandler, useValue: SentrySibling.createErrorHandler() },
    { provide: SentrySibling.TraceService, deps: [Router] },
    {
      provide: APP_INITIALIZER,
      useFactory: delayBootstrapping,
      deps: [LoadConfigurationService, SentrySibling.TraceService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    PortfoliosService,
    // ngx-mqtt providers (required for manual connection)
    { provide: MqttServiceConfig, useValue: { connectOnCreate: false } },
    { provide: MqttClientService, useValue: undefined },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    provideQueryClient(queryClient as any),
    ...(environment.production ? [] : [provideQueryDevTools()]),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
